<template>
  <v-container fluid>
    <v-alert
      v-if="form.applicant.verifier_remarks"
      :type="form.applicant.verifier_rejected === 'Y' ? 'error' : 'info'"
      outlined
      dense
      class="mb-2"
    >
      <strong>Verifier Remarks</strong>
      {{ form.applicant.verifier_remarks }}
    </v-alert>
    <v-card class="mb-2" outlined v-if="Object.keys(form.applicant).length">
      <!-- Applicant Details -->
      <v-card-title
        class="primary white--text text-subtitle-2 pa-2 pl-6 font-weight-bold"
      >
        Applicant Details
      </v-card-title>
      <v-card-text class="mt-2 px-4">
        <div class="custom-grid">
          <!-- Profile Photo -->
          <div class="grid-item div1">
            <ProfileUpload
              v-model="form.applicant.photoImage"
              :photo="form.applicant.photo"
              :declaration="true"
              :isReadOnly="true"
            />
          </div>

          <!-- DEB ID -->
          <div class="grid-item div3">
            <div class="field-container accent2">
              <span class="field-label">DEB ID</span>
              <span class="field-value">{{
                form.applicant.deb_id || "-"
              }}</span>
            </div>
          </div>

          <!-- ABC ID -->
          <div class="grid-item div4">
            <div class="field-container accent2">
              <span class="field-label">ABC ID</span>
              <span class="field-value">{{
                form.applicant.abc_id || "-"
              }}</span>
            </div>
          </div>

          <div class="grid-item div5">
            <div class="field-container accent2">
              <span class="field-label"
                >Name(As per the latest certificate)</span
              >
              <span class="field-value">{{ form.applicant.name }}</span>
            </div>
          </div>

          <div class="grid-item div6">
            <div class="field-container accent2">
              <span class="field-label">Name(As per DEB)</span>
              <span class="field-value">{{
                form.applicant?.deb_map?.name || "-"
              }}</span>
            </div>
          </div>

          <!-- DOB -->
          <div class="grid-item div7">
            <div class="field-container accent2">
              <span class="field-label">Date of Birth</span>
              <span class="field-value">{{
                formatDob(form.applicant.dob)
              }}</span>
            </div>
          </div>

          <!-- Programme -->
          <div class="grid-item div8">
            <div class="field-container accent2">
              <span class="field-label">Programme</span>
              <span class="field-value">{{
                getProgramName(form.applicant.program_id)
              }}</span>
            </div>
          </div>
          <!-- Dynamic fields -->
          <template v-for="(value, key) in Object.entries(form.applicant)">
            <template v-if="isObject(value[1], value[0])">
              <div
                class="grid-item section-title primary--text"
                :key="`title-${key}`"
              >
                <span v-if="replaceName(value[0]) != 'Deb Map'">
                  {{ replaceName(value[0]) }}</span
                >
              </div>
              <template v-for="(subValue, subKey) in value[1]">
                <div
                  class="grid-item field-item"
                  v-if="getLabel(subKey)"
                  :key="`${key}-${subKey}`"
                >
                  <div class="field-container accent2">
                    <span class="field-label">{{ getLabel(subKey) }}</span>
                    <span
                      class="field-value"
                      v-if="
                        subKey === 'relationship' &&
                        form.applicant.family.relationship === 'Other'
                      "
                    >
                      {{ form.applicant.family.relation_type }}
                    </span>
                    <span
                      class="field-value"
                      v-else-if="subKey === 'mobile_no'"
                    >
                      {{ form.applicant.family.mobile_prefix }}
                      {{ form.applicant.family.mobile_no }}
                    </span>
                    <span class="field-value" v-else>
                      {{ formatValue(subKey, subValue) }}
                    </span>
                  </div>
                </div>
              </template>
            </template>
            <div
              class="grid-item field-item"
              v-else-if="getLabel(value[0]) && value[0] !== 'name'"
              :key="value[0]"
            >
              <div class="field-container accent2">
                <span class="field-label">{{ getLabel(value[0]) }}</span>
                <span class="field-value">{{
                  formatValue(value[0], value[1])
                }}</span>
              </div>
            </div>
          </template>
        </div>
      </v-card-text>
    </v-card>

    <!-- Address Details -->
    <v-card class="mb-2" outlined v-if="Object.keys(form.address).length">
      <v-card-title
        class="primary white--text text-subtitle-2 pa-2 pl-6 font-weight-bold"
      >
        Address Details
      </v-card-title>
      <v-card-text class="mt-2">
        <v-row dense>
          <template v-for="(value, key) in Object.entries(form.address)">
            <template v-if="isObject(value[1], value[0])">
              <!-- Section Title -->
              <v-col cols="12" :key="'section-' + key">
                <div class="section-title primary--text">
                  {{ replaceName(value[0]) }}
                </div>
              </v-col>

              <!-- Combined comma-separated address values -->
              <v-col cols="12" :key="'values-' + key">
                <div class="field-container accent2">
                  <span class="field-label">{{ getLabel(value[0]) }}</span>
                  <span class="field-value">
                    {{
                      Object.entries(value[1])
                        .filter(([subKey, _]) => getLabel(subKey))
                        .map(([k, v]) =>
                          formatValue(k, v) !== "-" ? formatValue(k, v) : "",
                        )
                        .filter(Boolean)
                        .join(", ")
                    }}
                  </span>
                </div>
              </v-col>
            </template>
          </template>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Education Details -->

    <v-card class="mb-2" outlined v-if="form.education.length">
      <v-card-title
        class="primary white--text text-subtitle-2 pa-2 pl-6 font-weight-bold"
      >
        Education Details
      </v-card-title>
      <v-card-text class="mt-2 px-4">
        <v-row dense>
          <template v-for="(education, index) in form.education">
            <v-col cols="12" sm="4" :key="'edu-inline-' + index">
              <div
                class="field-container accent2"
                v-if="education.qualification_name != 'Kovida'"
              >
                <div class="field-label">
                  {{ education.qualification_name.toUpperCase() }}
                </div>
                <div class="field-value">
                  {{
                    education.name_of_institution
                      ? education.name_of_institution
                      : "-"
                  }}
                </div>
              </div>
            </v-col>
          </template>
        </v-row>
      </v-card-text>
    </v-card>
    <!-- Verifier Action Buttons: only the Verifier, only before verification is done -->
    <v-row
      class="form-row no-print mt-5 mb-4"
      v-if="
        currentUserRole === 'Verifier' &&
        form.applicant.reviewer_level !== 1 &&
        (form.applicant.application_status == 'Submitted' ||
          form.applicant.application_status == 'Re-Submitted')
      "
    >
      <v-col cols="12" class="d-flex justify-end pa-0">
        <v-btn
          class="mr-4"
          color="success"
          @click="(statusText = 'Verify'), openConfirmation()"
        >
          <v-icon class="mr-2">mdi-check-decagram</v-icon>
          Verify
        </v-btn>
        <v-btn
          class="mr-4"
          color="warning"
          @click="(statusText = 'Pending'), openConfirmation()"
        >
          <v-icon class="mr-2">mdi-clock-outline</v-icon>
          Pending
        </v-btn>
        <v-btn
          class="mr-4"
          color="error"
          @click="(statusText = 'Rejected'), openConfirmation()"
        >
          <v-icon class="mr-4">mdi-close-circle</v-icon>
          Reject
        </v-btn>
      </v-col>
    </v-row>

    <!-- Reviewer Action Buttons: hidden from Verifier; blocked until reviewer_level == 1 -->
    <v-row
      class="form-row no-print mt-5 mb-4"
      v-if="
        currentUserRole !== 'Partner' &&
        currentUserRole !== 'Business' &&
        currentUserRole !== 'CFO' &&
        currentUserRole !== 'Management' &&
        currentUserRole !== 'Business Reviewer' &&
        currentUserRole !== 'Coordinator' &&
        currentUserRole !== 'Verifier' &&
        form.applicant.application_status != 'Draft' &&
        form.applicant.application_status != 'Approved' &&
        form.applicant.application_status != 'Admitted' &&
        form.applicant.application_status != 'Rejected' &&
        form.applicant.partner_approval_status != 'Rejected'
      "
    >
      <v-col cols="12" class="d-flex justify-end pa-0">
        <!-- v-if="form.applicant.completion_status == 'Awaiting Result'" -->
        <v-btn
          v-if="form.applicant.application_status != 'Provisionally Approved'"
          class="mr-4"
          color="warning"
          @click="checkVerifiedThenOpen('Provisionally Approved')"
        >
          <v-icon class="mr-4">mdi-alert-circle</v-icon>
          Provisionally Approve
        </v-btn>
        <!-- v-if="form.applicant.application_status != 'Provisionally Approved'" -->
        <v-btn
          class="mr-4"
          color="success"
          @click="checkVerifiedThenOpen('Pending')"
        >
          <v-icon class="mr-2">mdi-clock-outline</v-icon>
          Pending
        </v-btn>
        <v-btn
          class="mr-4"
          color="success"
          :disabled="form.applicant.partner_approval_status == 'Pending'"
          @click="
            !isReviewerVerified()
              ? warnNotVerified()
              : ((statusText = 'Approved'),
                !form.applicant.deb_validated &&
                (!form.applicant.deb_map ||
                  Object.keys(form.applicant.deb_map).length === 0)
                  ? openWarning()
                  : openConfirmation())
          "
        >
          <v-icon class="mr-2">mdi-check-circle</v-icon>
          Approve
        </v-btn>
        <v-btn
          v-if="form.applicant.application_status != 'Provisionally Approved'"
          class="mr-4"
          color="error"
          :disabled="form.applicant.partner_approval_status == 'Pending'"
          @click="checkVerifiedThenOpen('Rejected')"
        >
          <v-icon class="mr-4">mdi-close-circle</v-icon>
          Reject
        </v-btn>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ dialogTitle }}
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-textarea
                class="mt-2"
                rows="2"
                dense
                label="Remarks"
                outlined
                v-model="remarks"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="dialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            :loading="loading"
            @click="
              statusText === 'Pending'
                ? updateRemarks()
                : updateApplicantStatus()
            "
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmationDialog" width="500" persistent>
      <confirmationDialogForm
        v-if="confirmationDialog"
        :confirmationText="confirmationText"
        @closeDialog="confirmationDialog = false"
        :loading="loading"
        :disableButton="hideButton"
        @saveDialog="
          statusText === 'Pending' ||
          statusText === 'Rejected' ||
          (currentUserRole === 'Verifier' && statusText === 'Verify')
            ? (dialog = true)
            : updateApplicantStatus()
        "
      />
    </v-dialog>
  </v-container>
</template>
<script>
import ProfileUpload from "./ProfileUpload.vue";
import confirmationDialogForm from "../confirmationDialog.vue";

export default {
  components: { ProfileUpload, confirmationDialogForm },

  data() {
    return {
      fieldLabels: {
        gender: "Gender",
        mobile_no: "Mobile Number",
        whatsapp_mobile_no: "WhatsApp Number",
        email: "Personal Email",
        alternate_email: "Alternate Email",
        country_id: "Nationality",
        religion_id: "Religion",
        caste_id: "Caste",
        degree_id: "Degree",
        community_id: "Reservation Category",
        residence: "Resident Status",
        indian_air_force: "Are you serving in Air Force",
        relationship: "Relation",
        name_of_institution: "Name of Institution",
        // board: "Board",
        // location: "Location",
        // result_type: "Result Type",
        // marks_obtained: "Marks Obtained",
        // total_marks: "Total Marks",
        // grade: "Grade",
        // is_indian_address: "Is Indian Address",
        address_line1: "Address Line 1",
        address_line2: "Address Line 2",
        city: "City",
        state: "State",
        country: "Country",
        postcode: "Postcode",
        // locality: "Locality",
        pan_card_no: "PAN Number",
        aadhar_card_no: "Aadhaar Number",
        passport_number: "Passport Number",
        passport_date_of_issue: "Passport Date of Issue",
        passport_place_of_issue: "Passport Place of Issue",
        name: "Name",
      },
      countrys: [],
      religions: [],
      castes: [],
      communitys: [],
      programs: [],
      form: {
        applicant: {},
        address: {},
        education: [],
      },
      remarks: "",
      statusText: "",
      dialog: false,
      confirmationDialog: false,
      confirmationText: "",
      loading: false,
      hideButton: false,
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  computed: {
    formattedDob() {
      if (!this.form.applicant.dob) return "";
      const [year, month, day] = this.form.applicant.dob.split("-");
      return `${day}-${month}-${year}`;
    },
    currentUserRole() {
      return this.$store.state.currentUser.role;
    },
    dialogTitle() {
      if (this.statusText === "Rejected") return "Reject";
      if (this.statusText === "Verify") return "Verify";
      return "Pending";
    },
  },
  watch: {},
  created() {
    this.form = JSON.parse(JSON.stringify(this.value));
    this.init();
  },
  methods: {
    async init() {
      await Promise.all([
        this.getReligions(),
        this.getCastes(),
        this.getCommunities(),
        this.getCountries(),
        this.getPrograms(),
      ]);
    },
    shouldDisplayField(subKey, education) {
      if (
        (education.result_type !== "grade" && subKey === "grade") ||
        (education.result_type === "grade" && subKey === "marks_obtained") ||
        (education.result_type !== "marks" && subKey === "total_marks")
      ) {
        return false;
      }
      return true;
    },
    async getPrograms() {
      try {
        const response = await this.$axios.get("/programs");
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },
    async getReligions() {
      try {
        const response = await this.$axios.get("/religions");
        this.religions = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCastes() {
      try {
        const response = await this.$axios.get("/castes");
        this.castes = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCommunities() {
      try {
        const response = await this.$axios.get("/communities");
        this.communitys = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCountries() {
      try {
        const response = await this.$axios.get("/countries");
        this.countrys = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },

    getLabel(key) {
      const residenceValue = Object.entries(this.form.applicant).find(
        ([key, value]) => key === "residence",
      )[1];
      if (residenceValue == "true") {
        if (
          this.fieldLabels[key] == "Passport Number" ||
          this.fieldLabels[key] == "Passport Date of Issue" ||
          this.fieldLabels[key] == "Passport Place of Issue"
        ) {
          return;
        }
      } else if (residenceValue == "false") {
        if (
          this.fieldLabels[key] == "Aadhaar Number" ||
          this.fieldLabels[key] == "PAN Number"
        ) {
          return;
        }
      }
      return this.fieldLabels[key];
    },
    replaceName(key) {
      return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatValue(key, value) {
      if (value === null || value === "") return "-";
      if (key === "residence") {
        return value === "true" ? "Resident Indian" : "Non Resident Indian";
      }
      if (typeof value === "boolean") return value ? "Yes" : "No";

      if (typeof value === "string") {
        if (value.toLowerCase() === "true") return "Yes";
        if (value.toLowerCase() === "false") return "No";
      }
      if (key === "result_type") {
        if (value == "cgpa") return "CGPA";
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
      if (key === "mobile_no") {
        const prefix = this.form.applicant.mobile_prefix || "";
        return `${prefix} ${value}`.trim();
      }
      if (key === "whatsapp_mobile_no") {
        const prefix = this.form.applicant.whatsapp_mobile_prefix || "";
        return `${prefix} ${value}`.trim();
      }
      // If key ends with `_id`, fetch the corresponding name from the respective list
      if (key.endsWith("_id")) {
        let listName = key.replace("_id", "s"); // Example: country_id → countries
        return this.getItemName(listName, value);
      }

      return value;
    },
    getItemName(listName, id) {
      if (!this[listName]) return id; // Return ID if the list doesn't exist
      let item = this[listName].find((item) => item.id === id);
      return item ? item.name : id; // Return name if found, otherwise fallback to ID
    },
    isObject(value, key) {
      if (
        [
          "aadhar",
          "pan",
          "ug",
          "pg",
          "sslc",
          "photo",
          "hsc",
          "employment_letter",
          "id_card",
          "kovida_certificate",
        ].includes(key)
      )
        return false;

      return (
        value && typeof value === "object" && !(value instanceof File) // Exclude File objects
      );
    },
    isReviewerVerified() {
      return this.form.applicant.reviewer_level === 1;
    },
    warnNotVerified() {
      this.$store.commit("showSnackbar", {
        message: "Not Verified Yet",
        color: "error",
      });
    },
    checkVerifiedThenOpen(status) {
      if (!this.isReviewerVerified()) {
        this.warnNotVerified();
        return;
      }
      this.statusText = status;
      this.openConfirmation();
    },
    async updateApplicantStatus() {
      this.loading = true;
      // A Verifier's "Reject" isn't a real rejection — it's a flag for the
      // Reviewer. Both a Verifier's Verify and Reject now behave the same
      // way on the backend (reviewer_level goes to 1, application_status is
      // left untouched) and both always carry remarks — only the
      // verifier_rejected flag ("Y"/"N") tells the Reviewer which button
      // was actually clicked.
      const isVerifierAction =
        this.currentUserRole === "Verifier" &&
        (this.statusText === "Verify" || this.statusText === "Rejected");

      let map = {
        type: this.currentUserRole === "Verifier" ? "Verify" : "Review",
      };
      if (isVerifierAction) {
        map.reviewer_level = 1;
        map.verifier_remarks = this.remarks;
        map.verifier_rejected = this.statusText === "Rejected" ? "Y" : "N";
      } else {
        map.application_status = this.statusText;
        if (this.statusText === "Rejected") {
          map.rejection_reason = this.remarks;
        }
      }
      this.$axios
        .put(`/verify_review_applicant/${this.form.applicant.id}`, map)
        .then(() => {
          this.dialog = false;
          this.confirmationDialog = false;
          this.$emit("update");
          this.loading = false;
        })
        .catch(async (error) => {
          this.loading = false;
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateRemarks() {
      this.loading = true;
      try {
        let map = {
          pending_reason: {
            applicant_detail_id: this.form.applicant.id,
            reason: this.remarks,
          },
        };

        await this.$axios.post(`/pending_reasons`, map);
        await this.updateApplicantStatus();
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      }
    },
    openWarning() {
      this.confirmationText = `Applicant provided Deb ID is invalid. Please request the applicant to provide a valid Deb ID after which the application can be approved.`;
      this.confirmationDialog = true;
      this.hideButton = true;
    },
    openConfirmation() {
      this.hideButton = false;
      const currentStatus = this.form.applicant.application_status;
      const targetStatus = this.statusText;

      if (this.currentUserRole === "Verifier" && targetStatus === "Verify") {
        this.confirmationText =
          "Are you sure you want to verify this application? Once verified, cannot be reverted.";
        this.confirmationDialog = true;
        return;
      }

      if (this.currentUserRole === "Verifier" && targetStatus === "Rejected") {
        this.confirmationText =
          "This will flag the application for the Reviewer with your reason — it will not reject the application. Continue?";
        this.confirmationDialog = true;
        return;
      }

      if (["Submitted", "Re-Submitted"].includes(currentStatus)) {
        this.confirmationText = `Are you sure you want to change the status to ${targetStatus.toLowerCase()}?`;
        this.confirmationDialog = true;
      } else if (currentStatus !== targetStatus) {
        this.confirmationText = `This application is currently in ${currentStatus.toLowerCase()} status. Are you sure you want to change it to ${targetStatus.toLowerCase()}?`;
        this.confirmationDialog = true;
      }
      // else if (["Pending", "Rejected"].includes(currentStatus)) {
      //   this.dialog = true;
      // }
      else {
        this.updateApplicantStatus();
      }
    },
    formatDob(dob) {
      if (!dob) return "";
      const [year, month, day] = dob.split("-");
      return `${day}-${month}-${year}`;
    },
  },
};
</script>

<style scoped>
/* Card Title */
.v-card-title {
  font-weight: bold;
  height: 40px !important;
}

/* Section Title */
.section-title {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 2px;
}

/* Field Container */
.field-container {
  padding: 5px;
  margin: 2px 2px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

/* Labels and Values */
.field-label {
  font-weight: bold;
  color: #424242;
  font-size: 13px;
}

.field-value {
  color: #616161;
  font-size: 13px;
}
.headline {
  font-size: 20px;
  font-weight: bold;
}

@media print {
  .no-print {
    display: none;
  }
}

.custom-grid {
  display: grid;
  grid-template-columns: 1fr; /* Default: Mobile (1 column) */
  gap: 6px;
}

/* Tablet ≥600px: 2 columns */
@media (min-width: 600px) {
  .custom-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Medium Desktop ≥960px: 3 columns */
@media (min-width: 960px) {
  .custom-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Large Desktop ≥1280px: 5 columns */
@media (min-width: 1280px) {
  .custom-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}

/* === Grid Item Layouts === */

/* Profile photo */
.div1 {
  grid-column: span 1;
  grid-row: span 2;
  display: flex;
  justify-content: center;
}

/* These items will flow naturally in grid — no fixed columns needed */
.div3,
.div4,
.div5,
.div6 {
  grid-column: span 1;
}

/* Section titles take full row */
.section-title {
  grid-column: 1 / -1;
  font-weight: 600;
  margin-top: 2px;
  font-size: 14px;
  color: #333;
}

/* Default field item */
.field-item {
  grid-column: span 1;
}
</style>
